import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { getControleDescargas } from '@spark/services/descarga';
import { useLatestAsyncRequest } from '@spark/ui-components/hooks/useLatestAsyncRequest';
import { mapControleDescarga, normalizeDescargaFilters } from '../../shared/controleDescargaMapper';
import { filterConfig } from '../config/filterConfig';

const buildInitialFilters = () => filterConfig.reduce((acc, filter) => {
  acc[filter.id] = filter.defaultValue ?? '';
  return acc;
}, {});

export const useDescargasListagem = (options = {}) => {
  const readyToFetch = options.readyToFetch !== false;
  const initialFiltersFromOptions = options.initialFiltros;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [filtros, setFiltros] = useState(initialFiltersFromOptions || buildInitialFilters());
  const [refreshKey, setRefreshKey] = useState(0);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 25
  });

  const { beginRequest, isCurrentRequest, isMounted } = useLatestAsyncRequest();

  const fetchData = useCallback(async () => {
    if (!isMounted() || !readyToFetch) return;

    const requestId = beginRequest();

    try {
      setLoading(true);

      const filters = {
        ...normalizeDescargaFilters(filtros),
        PaginaAtual: paginationState.pageIndex + 1,
        QuantidadePorPagina: paginationState.pageSize
      };

      const response = await getControleDescargas(filters);
      if (!isCurrentRequest(requestId)) return;

      const responseData = response?.data || response || {};
      const registros = Array.isArray(responseData.registros) ? responseData.registros : [];
      const dadosProcessados = registros.map(mapControleDescarga);

      setData(dadosProcessados);
      setTotalRegistros(responseData.totalRegistros || dadosProcessados.length);
    } catch (error) {
      if (!isCurrentRequest(requestId)) return;
      console.error('Erro ao buscar descargas:', error);
    } finally {
      if (isCurrentRequest(requestId)) {
        setLoading(false);
        setFilterLoading(false);
      }
    }
  }, [beginRequest, filtros, isCurrentRequest, isMounted, paginationState.pageIndex, paginationState.pageSize, readyToFetch, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterApply = useCallback((newFilters = {}) => {
    // Batch explicito: fora de event handlers (ex.: aplicacao assincrona da
    // configuracao padrao) o React 17 nao agrupa os setStates, e filtros +
    // refreshKey disparariam o fetch duas vezes.
    unstable_batchedUpdates(() => {
      setFiltros(newFilters);
      setFilterLoading(true);
      setPaginationState((prev) => ({
        ...prev,
        pageIndex: 0
      }));
      setRefreshKey((prev) => prev + 1);
    });
  }, []);

  const applyConfiguracaoFiltros = useCallback((configFiltros = {}) => {
    const mergedFilters = {
      ...buildInitialFilters(),
      ...configFiltros
    };

    handleFilterApply(mergedFilters);
  }, [handleFilterApply]);

  const handlePaginationChange = useCallback((next) => {
    setLoading(true);
    setPaginationState((prev) => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  const state = useMemo(() => ({
    data,
    loading,
    filterLoading,
    totalRegistros,
    filtros,
    paginationState
  }), [data, loading, filterLoading, totalRegistros, filtros, paginationState]);

  return {
    ...state,
    handleFilterApply,
    applyConfiguracaoFiltros,
    handlePaginationChange,
    fetchData
  };
};
